import { useState } from 'react';
import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import { ProductsResponse } from '@/features/editor/widgets/custom-widget/loading-section/manual-product-picker/context/model';
import { StorePage } from '@/features/editor/context/use-device-navigation';
import { loomi } from '@/webapi/generated/catalog-proto.js';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

export const UPSELLS_APP_ID = `upsells`;

export function useCatalogApi() {
  const binaryCatalogEnabled = useFeatureBit(
    FeatureBit.CATALOG_BINARY_RELATIONS,
  );
  const [binaryLoading, setBinaryLoading] = useState(false);
  const { post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    },
  }));

  const listEntireCatalog = async (page: number, pageSize: number) => {
    // Keep the original catalog response available as a feature-flag rollback.
    if (!binaryCatalogEnabled) {
      return listLegacyCatalog(post, page, pageSize);
    }

    setBinaryLoading(true);

    try {
      const response = await fetch(routes.listCatalogBinary(), {
        method: `POST`,
        credentials: `include`,
        headers: {
          Accept: `application/octet-stream`,
          'Content-Type': `application/json`,
        },
        body: JSON.stringify({ page, pageSize }),
      });
      if (!response.ok) {
        throw new Error(`Binary catalog returned ${response.status}`);
      }
      const message = loomi.binary.Catalog.decode(
        new Uint8Array(await response.arrayBuffer()),
      );
      return catalogMessageToResponse(message);
    } catch (_error) {
      // A mixed deployment must still work when the client is newer than svc-catalog.
      return listLegacyCatalog(post, page, pageSize);
    } finally {
      setBinaryLoading(false);
    }
  };

  const searchCatalog = (query: string, maxResults: number, domain?: string) =>
    post(routes.catalogSearch(), {
      query,
      maxResults,
      domain,
    } as SearchCatalogRequest) as Promise<SearchCatalogResponse>;

  const listProductsByText = (
    page: number,
    size: number,
    options: { query: string },
  ) =>
    post(routes.listRecommendationProducts(), {
      page,
      size,
      query: options?.query || ``,
    } as any) as Promise<ProductsResponse>;

  const listProductsByIds = (productIds: Array<string>) =>
    post(routes.listRecommendationProducts(), {
      page: 0,
      size: 25,
      productIds,
    } as any) as Promise<ProductsResponse>;

  const listProducts = (searchText: string, page?: number) =>
    post(routes.listRecommendationProducts(), {
      page: page || 0,
      size: 25,
      query: searchText,
    } as any) as Promise<ProductsResponse>;

  return {
    loading: binaryCatalogEnabled ? binaryLoading : loading,
    listEntireCatalog,
    searchCatalog,
    listProductsByText,
    listProductsByIds,
    listProducts,
  };
}

function listLegacyCatalog(
  post: (url: string, body: unknown) => Promise<unknown>,
  page: number,
  pageSize: number,
): Promise<CatalogResponse> {
  return post(routes.listCatalog(), {
    page,
    pageSize,
  }) as Promise<CatalogResponse>;
}

function catalogMessageToResponse(
  message: loomi.binary.Catalog,
): CatalogResponse {
  return {
    products: message.products.map((product) => ({
      id: product.id.toString(),
      collections: product.collections,
      handle: product.handle,
      product_type: product.productType,
      title: product.title,
      image: {
        src: product.image?.src || ``,
        height: product.image?.height || 0,
        width: product.image?.width || 0,
      },
      tags: product.tags,
      rank: product.rank,
      options: product.options.map((option) => ({
        name: option.name || ``,
        values: option.values || [],
      })),
      variantIds: product.variantIds.map((id) => Number(id)),
    })),
    collections: message.collections.map((collection) => ({
      id: Number(collection.id),
      handle: collection.handle,
      title: collection.title,
    })),
    productTags: message.productTags,
    customerTags: message.customerTags,
    reviewsEnabled: message.reviewsEnabled,
    currencies: message.currencies,
    productCount: message.productCount,
    mainAttribute: message.mainAttribute,
    mainAttributeOptions: message.mainAttributeOptions,
    productTagDictionary: message.productTagDictionary,
    productTagRelationData: message.productTagRelationData,
    productTagRelationOffsets: message.productTagRelationOffsets,
    productTagRelationFormats: message.productTagRelationFormats,
    productCollectionDictionary: message.productCollectionDictionary,
    productCollectionRelationData: message.productCollectionRelationData,
    productCollectionRelationOffsets: message.productCollectionRelationOffsets,
    productCollectionRelationFormats: message.productCollectionRelationFormats,
  };
}

export interface SearchCatalogRequest {
  query: string;
  maxResults: number;
  domain?: string;
}

export interface SearchCatalogResponse {
  pages: StorePage[];
  total: number;
}

export enum StorePageType {
  STORE_PAGE_UNKNOWN = 0,
  STORE_PAGE_HOMEPAGE = 1,
  STORE_PAGE_PRODUCT = 2,
  STORE_PAGE_COLLECTION = 3,
  STORE_PAGE_CART = 4,
  STORE_PAGE_CHECKOUT = 5,
  STORE_PAGE_CUSTOM = 6,
}

export interface CatalogResponse {
  products: Array<Product>;
  collections: Array<Collection>;
  productTags: Array<string>;
  customerTags: Array<string>;
  reviewsEnabled?: boolean;
  currencies: Array<string>;
  productCount: number;
  mainAttribute: string;
  mainAttributeOptions: Array<string>;
  productTagDictionary?: Array<string>;
  productTagRelationData?: Uint8Array | string;
  productTagRelationOffsets?: Uint8Array | string;
  productTagRelationFormats?: Uint8Array | string;
  productCollectionDictionary?: Array<string>;
  productCollectionRelationData?: Uint8Array | string;
  productCollectionRelationOffsets?: Uint8Array | string;
  productCollectionRelationFormats?: Uint8Array | string;
}

export interface Collection {
  id: number;
  handle: string;
  title: string;
}

export interface Product {
  id: string;
  collections?: string[];
  handle: string;
  product_type: string;
  title: string;
  image: {
    src: string;
    height: number;
    width: number;
  };
  tags?: string[];
  rank: number;
  options: ProductOption[];
  variantIds: number[];
}

export interface ProductOption {
  name: string;
  values: string[];
}
